import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusChip } from "@/components/StatusChip";
import { MoneyDelta, PipsDelta, CostsLine } from "@/components/MoneyDelta";
import { DecisionBlock } from "@/components/DecisionBlock";
import { formatDateTime, formatPrice } from "@/lib/format";

export function SignalDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ["signal", id], queryFn: () => api.signal(id), enabled: Number.isFinite(id) });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["signal", id] });
    void queryClient.invalidateQueries({ queryKey: ["signals"] });
  };

  const approve = useMutation({ mutationFn: () => api.approveSignal(id), onSuccess: invalidate });
  const close = useMutation({ mutationFn: () => api.closeSignal(id), onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: () => api.cancelSignal(id), onSuccess: invalidate });

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">Could not load signal #{id}.</p>;
  }

  const { signal, channel, message, orders, deals, decisions, followUps } = query.data;
  const symbol = signal.brokerSymbol ?? signal.symbol ?? signal.symbolRaw;
  const pending = approve.isPending || close.isPending || cancel.isPending;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold">
            {symbol} {signal.direction?.toUpperCase()}
          </h1>
          <StatusChip status={signal.status} />
        </div>
        <p className="text-sm text-muted-foreground">
          {channel.title} · {formatDateTime(signal.createdAt)}
        </p>
        {signal.directionCorrected && (
          <p className="mt-1 text-xs text-yellow-500">
            Direction corrected: channel said {signal.direction === "buy" ? "SELL" : "BUY"}, executed as{" "}
            {signal.direction?.toUpperCase()} — TP/SL geometry disagreed with the stated direction.
          </p>
        )}
        {signal.skipReason && <p className="mt-1 text-xs text-muted-foreground">Skipped: {signal.skipReason}</p>}
      </div>

      <div className="flex gap-2">
        {signal.status === "awaiting_approval" && (
          <Button size="sm" onClick={() => approve.mutate()} disabled={pending}>
            Approve
          </Button>
        )}
        {["live", "executing"].includes(signal.status) && (
          <>
            <Button size="sm" variant="destructive" onClick={() => close.mutate()} disabled={pending}>
              Close
            </Button>
            <Button size="sm" variant="outline" onClick={() => cancel.mutate()} disabled={pending}>
              Cancel pending
            </Button>
          </>
        )}
      </div>

      <Card>
        <CardContent className="space-y-2 p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Entry" value={formatPrice(signal.entryPrice)} />
            <Field label="Stop loss" value={formatPrice(signal.stopLoss)} />
            <Field label="Take profits" value={(signal.takeProfits ?? []).map(formatPrice).join(" / ") || "—"} />
            <Field label="Confidence" value={signal.confidence !== null ? `${Math.round(signal.confidence * 100)}%` : "—"} />
          </div>
          <div className="flex items-center gap-4 border-t border-border pt-2 text-sm">
            <span className="text-muted-foreground">Realized:</span>
            <MoneyDelta value={signal.profitMoney} />
            <PipsDelta value={signal.pipsRealized} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Original message</CardTitle>
        </CardHeader>
        <CardContent>
          {message.deletedAt && (
            <p className="mb-2 text-xs font-medium text-destructive">
              Deleted by the channel at {formatDateTime(message.deletedAt)} — content preserved below.
            </p>
          )}
          <p className="whitespace-pre-wrap text-sm">{message.text}</p>
          {message.editedAt && <p className="mt-2 text-xs text-muted-foreground">Edited {formatDateTime(message.editedAt)}</p>}
        </CardContent>
      </Card>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="decisions">Reasoning ({decisions.length})</TabsTrigger>
          <TabsTrigger value="followups">Follow-ups ({followUps.length})</TabsTrigger>
          <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-2">
          {orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
          {orders.map((order) => (
            <Card key={order.id}>
              <CardContent className="flex items-center justify-between gap-2 p-3">
                <div className="text-sm">
                  <div className="font-medium">
                    Slot {order.slot ?? "—"} · {order.volume} lots
                  </div>
                  <div className="text-xs text-muted-foreground">
                    SL {formatPrice(order.sl)} · TP {formatPrice(order.tp)}
                    {order.closeReason && ` · closed: ${order.closeReason}`}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusChip status={order.state} />
                  <MoneyDelta value={order.profit} />
                  <CostsLine swap={order.swap} commission={order.commission} />
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="decisions" className="space-y-2">
          {decisions.length === 0 && <p className="text-sm text-muted-foreground">No decisions recorded.</p>}
          {decisions.map((decision) => (
            <DecisionBlock key={decision.id} decision={decision} />
          ))}
        </TabsContent>

        <TabsContent value="followups" className="space-y-2">
          {followUps.length === 0 && <p className="text-sm text-muted-foreground">No follow-ups on this signal.</p>}
          {followUps.map((event) => (
            <Card key={event.id}>
              <CardContent className="space-y-1 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{event.type.replace(/_/g, " ")}</span>
                  <StatusChip status={event.status} />
                </div>
                {event.skipReason && <p className="text-xs text-muted-foreground">{event.skipReason}</p>}
                {event.resolvedBy && <p className="text-xs text-muted-foreground">Resolved by: {event.resolvedBy.replace(/_/g, " ")}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="deals" className="space-y-2">
          {deals.length === 0 && <p className="text-sm text-muted-foreground">No deals yet.</p>}
          {deals.map((deal) => (
            <Card key={`${deal.id}:${deal.entryType}`}>
              <CardContent className="flex items-center justify-between gap-2 p-3 text-sm">
                <div>
                  <div className="font-medium">
                    {deal.entryType === "DEAL_ENTRY_IN" ? "Open" : "Close"} · {formatPrice(deal.price)}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(deal.dealTime)}</div>
                </div>
                <MoneyDelta value={deal.profit} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}
